import type { ProjectMilestone } from "../../../../types/project-phases.types.tsx"

export type MilestoneAmountField =
  | "planned_receivable"
  | "actual_received"
  | "planned_payable"
  | "actual_paid"

export interface ContributingMilestone {
  milestone: ProjectMilestone
  amount: number
}

/** Only the two "exceeded" rules have a milestone figure that explains an
 * overage — a mismatch can be an under-allocation, so it has no such field. */
export const AMOUNT_FIELD: Record<string, MilestoneAmountField> = {
  "contract_value_exceeded:planned": "planned_receivable",
  "contract_value_exceeded:actual": "actual_received",
  "contract_expenses_exceeded:planned": "planned_payable",
  "contract_expenses_exceeded:actual": "actual_paid"
}

/**
 * The smallest, largest-first set of milestones whose amounts explain the
 * overage — not every milestone with a nonzero figure, and never one with
 * $0 or a negative amount (a credit line cannot be "contributing" to an
 * overrun).
 */
export function selectContributingMilestones(
  milestones: ProjectMilestone[],
  amountField: MilestoneAmountField,
  excess: number
): ContributingMilestone[] {
  if (excess <= 0) return []

  const ranked = milestones
    .map((milestone) => ({
      milestone,
      amount: Number(milestone[amountField] ?? 0)
    }))
    .filter((entry) => entry.amount > 0)
    .sort((a, b) => b.amount - a.amount)

  const selected: ContributingMilestone[] = []
  let runningTotal = 0

  for (const entry of ranked) {
    if (runningTotal >= excess) break
    selected.push(entry)
    runningTotal += entry.amount
  }

  return selected
}
